import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Upload, Loader2, Search } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

/* Admin-only quick-upload button shown under the workspace tiles row.
   Lets the admin pick any leaf node from the workspace tree and attach a file
   without having to drill down through the hub dialogs. */
export default function QuickUploadButton() {
  const [open, setOpen] = useState(false);
  const [tree, setTree] = useState([]);
  const [query, setQuery] = useState('');
  const [target, setTarget] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const adminToken = typeof window !== 'undefined' ? localStorage.getItem('admin_token') : null;

  useEffect(() => {
    if (!open || tree.length) return;
    const auth = adminToken ? { headers: { Authorization: `Bearer ${adminToken}` }, withCredentials: true } : {};
    axios.get(`${API}/workspace/tree`, auth).then(({ data }) => setTree(data)).catch(() => setTree([]));
  }, [open, adminToken, tree.length]);

  if (!adminToken) return null;

  // Path string for any node, walking up to root
  const pathOf = (n) => {
    const ids = {};
    tree.forEach((x) => { ids[x.id] = x; });
    const parts = [];
    let cur = n;
    while (cur) { parts.unshift(cur.label); cur = ids[cur.parent_id]; }
    return parts.join(' › ');
  };

  // Only leaves accept uploads (documents OR inventory — both render docs UI)
  const leaves = tree.filter((n) => n.type === 'documents' || n.type === 'inventory');
  const filtered = query
    ? leaves.filter((n) => pathOf(n).toLowerCase().includes(query.toLowerCase()))
    : leaves;

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length || !target) return;
    setBusy(true);
    let ok = 0;
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      try {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('keep_raw', 'true');
        // eslint-disable-next-line no-await-in-loop
        const { data: up } = await axios.post(`${API}/admin/upload`, fd, {
          headers: { Authorization: `Bearer ${adminToken}`, 'Content-Type': 'multipart/form-data' },
          withCredentials: true,
        });
        // iter-122 — Multi-upload skips per-file title prompt; uses
        // filename as title. Single-file flow still prompts.
        const title = files.length === 1
          ? (window.prompt('Document title:', file.name.replace(/\.[^.]+$/, '')) || file.name)
          : file.name.replace(/\.[^.]+$/, '');
        // eslint-disable-next-line no-await-in-loop
        await axios.post(`${API}/admin/workspace/documents`, {
          node_id: target.id,
          title,
          file_url: `${BACKEND}${up.url}`,
          file_name: file.name,
          file_type: (file.name.split('.').pop() || '').toLowerCase(),
          order: i,
        }, { headers: { Authorization: `Bearer ${adminToken}` }, withCredentials: true });
        ok += 1;
      } catch (err) {
        console.error('Quick upload failed for', file.name, err);
      }
    }
    alert(`Uploaded ${ok}/${files.length} ${ok === 1 ? 'document' : 'documents'} to ${pathOf(target)}`);
    setOpen(false);
    setTarget(null);
    setQuery('');
    setBusy(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  return (
    <>
      <button
        data-testid="quick-upload-btn"
        onClick={() => setOpen(true)}
        className="flex items-center gap-2 px-5 py-2.5 rounded-full text-xs font-bold text-white transition-all hover:-translate-y-0.5 hover:shadow-lg"
        style={{ backgroundColor: '#003DA5', fontFamily: 'Manrope', boxShadow: '0 4px 14px rgba(0,61,165,0.25)' }}
      >
        <Upload className="w-4 h-4" /> Upload Document
      </button>

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setTarget(null); setQuery(''); } }}>
        <DialogContent className="max-w-lg p-6" data-testid="quick-upload-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl flex items-center gap-2" style={{ color: '#003DA5', fontFamily: "'Cormorant Garamond', serif" }}>
              <Upload className="w-5 h-5" /> Quick Upload
            </DialogTitle>
          </DialogHeader>

          {!target ? (
            <>
              <p className="text-xs mt-1 mb-3" style={{ color: '#7A8C8C', fontFamily: 'Manrope' }}>
                Pick the destination folder. The document will appear inside that tile and inventory form for everyone with access.
              </p>
              <div className="relative mb-3">
                <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2" style={{ color: '#7A8C8C' }} />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search folders…"
                  className="w-full pl-9 pr-3 py-2 rounded-lg border text-sm outline-none"
                  style={{ borderColor: '#A3C4EE', fontFamily: 'Manrope', backgroundColor: 'white' }}
                  data-testid="quick-upload-search"
                />
              </div>
              <div className="max-h-72 overflow-y-auto rounded-lg" style={{ border: '1px solid #D9E3F1' }}>
                {filtered.length === 0 ? (
                  <div className="p-6 text-center text-xs" style={{ color: '#7A8C8C', fontFamily: 'Manrope' }}>No folders match.</div>
                ) : (
                  filtered.map((n) => (
                    <button
                      key={n.id}
                      onClick={() => setTarget(n)}
                      data-testid={`quick-upload-target-${n.id}`}
                      className="w-full text-left px-3 py-2 text-xs hover:bg-blue-50 transition-colors border-b last:border-b-0"
                      style={{ borderColor: '#EEF3F9', fontFamily: 'Manrope', color: '#1A2E2E' }}
                    >
                      {pathOf(n)}
                    </button>
                  ))
                )}
              </div>
            </>
          ) : (
            <>
              <div className="rounded-lg p-3 mb-4" style={{ backgroundColor: '#F0F4FA', border: '1px solid #D9E3F1' }}>
                <div className="text-[10px] font-bold tracking-[0.15em] uppercase mb-0.5" style={{ color: '#003DA5', fontFamily: 'Manrope' }}>Destination</div>
                <div className="text-sm font-bold" style={{ fontFamily: 'Manrope', color: '#1A2E2E' }}>{pathOf(target)}</div>
                <button onClick={() => setTarget(null)} className="text-[10px] font-bold mt-1.5" style={{ color: '#003DA5', fontFamily: 'Manrope' }}>← change folder</button>
              </div>
              <input ref={fileRef} type="file" multiple className="hidden" onChange={handleUpload} accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.ppt,.pptx,.dwg,.dxf,.txt" />
              <button
                onClick={() => fileRef.current?.click()}
                disabled={busy}
                data-testid="quick-upload-pick-file"
                className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-bold text-white disabled:opacity-50"
                style={{ backgroundColor: '#003DA5', fontFamily: 'Manrope' }}
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {busy ? 'Uploading…' : 'Choose file & upload'}
              </button>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
